import { useState } from "react";
import Cookies from "js-cookie";
import {
  RegistrationCreateRequestBody,
  useRegistrationCreate,
} from "@gocardless/api/dashboard/registration";
import { useClearSWR } from "@gocardless/api/utils/use-clear-cache";
import { useLingui } from "@lingui/react";
import { useRouter } from "next/router";

import {
  SignupFormData,
  OB_SEARCH_QUERY_KEY,
  RegistrationParams,
  RegistrationResponse,
} from "./consts";

import { Route, getRouteURL } from "src/common/routing";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { setAccessToken } from "src/common/authorisation/session";
import {
  GA4Events,
  sendGA4Event,
} from "src/technical-integrations/third-party-tracking/google-analytics";
import {
  REDDIT_TRACKING_EVENT,
  trackReddit,
} from "src/technical-integrations/third-party-tracking/Reddit";
import { CountryCodeSupported } from "src/common/country";
import { COUNTRY_CODES } from "src/common/constants/countryCodes";
import { getItem, removeItem } from "src/common/local-storage/local-storage";
import {
  isCountryCodeSupported,
  isSandboxEnvironment,
} from "src/common/consts";
import { TrackingEvent } from "src/common/trackingEvents";
import { CookieStorage } from "src/common/constants/storage";
import { useHttpErrorHandler } from "src/components/routes/SignUp/optimised-signup/helper";
import { ShorthandLocale, useI18n } from "src/technical-integrations/i18n";

interface UseRegistrationProps {
  defaultCountryCode: CountryCodeSupported;
  registrationParams: RegistrationParams;
  setShowSignupForm: (arg: boolean) => void;
}

interface UseRegistration {
  submitEmailVerificationRegistration: (verificationCode: string) => void;
  isSubmitting: boolean;
  setShowSignupForm: (arg: boolean) => void;
}

export function useRegistration({
  registrationParams,
  setShowSignupForm,
}: UseRegistrationProps): UseRegistration {
  const { i18n } = useLingui();
  const clearCache = useClearSWR();
  const { sendEvent, group, identify } = useSegment();
  const { handleError, handleNoResponse } =
    useHttpErrorHandler(setShowSignupForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userLocale] = useI18n();
  const router = useRouter();

  const {
    email,
    geo,
    terms_and_conditions,
    creditor_type,
    accountType,
    referralCode,
    referralGeo,
    verification_method,
    hasFsbMemberDiscount,
    referralSource,
    referralCodeName,
    referralEmailVariation,
  } = registrationParams;

  const item = getItem(OB_SEARCH_QUERY_KEY);
  const referred_from = item ? { referred_from: "bank_account_data" } : null;

  // referral_code is ignored if in sandbox or not in an allowed geo
  const hasReferralCode = !!(
    !isSandboxEnvironment &&
    referralCode &&
    referralGeo &&
    isCountryCodeSupported(referralGeo) &&
    referralGeo !== CountryCodeSupported.DE
  );

  const preSignupDeviceID = Cookies.get(CookieStorage.PreSignupOptimizelyID);
  const gaClientID = (Cookies.get(CookieStorage.GACookieClientID) || "")
    .split(".")
    .slice(-2)
    .join(".");
  const impactClickID = Cookies.get(CookieStorage.ImpactClickID);
  const awinClickID = Cookies.get(CookieStorage.AWinClickID);
  const language = ShorthandLocale[userLocale];

  const countryName =
    COUNTRY_CODES(i18n).find(({ code }) => code === geo)?.name || "";

  const [registrationCreate] = useRegistrationCreate({
    onSuccess: async (response) => {
      try {
        if (!response) {
          throw new Error("No response from API");
        }
        const res = response as RegistrationResponse;
        setAccessToken(res.linked, true);
        const userDetails = res.linked.temporary_access_tokens?.links;
        const organisationID = res.registrations.links.organisation;
        const signUpDate = (
          res.registrations.organisation_created_at ?? new Date().toISOString()
        )?.substring(0, 10);
        const cpvEnabled = !!res.registrations.cpv_enabled;

        if (userDetails?.user) {
          await identify(userDetails?.user, {
            creditor_id: userDetails?.creditor,
            organisation_id: organisationID,
            pre_signup_device_id: preSignupDeviceID,
            ga_client_id: gaClientID,
            created_at: signUpDate,
          });
        }
        await group(organisationID, {
          created_at: signUpDate,
          signup_source: "website",
          organisation_id: organisationID,
          merchant_type: creditor_type,
          country_name: countryName,
          has_been_referred: hasReferralCode,
          ...referred_from,
        });
        if (referred_from) {
          removeItem(OB_SEARCH_QUERY_KEY);
        }
        sendEvent(TrackingEvent.SIGNUP_ACCOUNT_CREATED, {
          has_referral_code: hasReferralCode,
          partner_name: null,
          organisation_id: organisationID,
          user_id: userDetails?.user,
          context: {
            referrer: {
              type: "impactRadius",
              id: impactClickID,
            },
          },
          category: "Sign - up",
          revenue: 5,
          creditor_type,
          business_location: geo,
          awinclickid: awinClickID,
          ...(referralEmailVariation && {
            referral_variation: referralEmailVariation,
          }),
          referral_source: referralSource,
          ...(referralCodeName && { referrer: referralCodeName }),
        });
        sendGA4Event(GA4Events.SignUpSuccess, {
          organisationId: organisationID,
          anonymous_client_id: preSignupDeviceID,
        });
        trackReddit().trackRedditEvent(REDDIT_TRACKING_EVENT.SIGNUP);

        clearCache();
        router.push(
          getRouteURL({
            route: cpvEnabled ? Route.Intro : Route.OnboardingWelcome,
            absolute: true,
          })
        );
      } catch (error) {
        await sendEvent(TrackingEvent.SIGNUP_SIGNUP_FAILED, {
          label: "No response from API",
          reason: "no_response",
          email,
          geo,
          creditor_type,
          verification_method,
        });

        handleNoResponse();
      }
    },
    onError: async (error) => {
      setIsSubmitting(false);
      handleError(error);
    },
  });

  const getRequestParams = (formData?: SignupFormData) => ({
    email: email || formData?.email,
    signup_details: {
      terms_and_conditions:
        terms_and_conditions || formData?.terms_and_conditions,
    },
    account_type: accountType,
    referral_code: referralCode,
    language,
    pre_signup_device_id: preSignupDeviceID,
    verification_method: verification_method || formData?.verification_method,
    has_fsb_member_discount: hasFsbMemberDiscount,
  });

  const submitEmailVerificationRegistration = (verificationCode: string) => {
    setIsSubmitting(true);
    const requestBody: RegistrationCreateRequestBody = {
      ...getRequestParams(),
      verification_code: verificationCode,
    };

    registrationCreate(requestBody);
  };

  return {
    submitEmailVerificationRegistration,
    isSubmitting,
    setShowSignupForm,
  };
}
